import { useState } from 'react';
import type { ChangeEvent, FC, FormEvent } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import './PhoneVerificationPage.css';

interface RegistrationData {
  email?: string;
  phoneNumber: string;
  phoneVerificationCode?: string;
  emailVerificationCode?: string;
}

interface ApiResponse {
  success: boolean;
  message?: string;
  phoneVerificationCode?: number | string;
}

const API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL ?? '');

function isRegistrationData(value: unknown): value is RegistrationData {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as {
    email?: unknown;
    phoneNumber?: unknown;
    phone_number?: unknown;
    phoneVerificationCode?: unknown;
    emailVerificationCode?: unknown;
  };
  return typeof candidate.phoneNumber === 'string' || typeof candidate.phone_number === 'string';
}

function normalizeRegistrationData(value: RegistrationData | (RegistrationData & { phone_number?: string })): RegistrationData {
  const candidate = value as RegistrationData & {
    phone_number?: string;
    phoneVerificationCode?: number | string;
    emailVerificationCode?: number | string;
  };
  const phoneFallbackCode = candidate.phoneVerificationCode;
  const emailFallbackCode = candidate.emailVerificationCode;
  return {
    email: candidate.email,
    phoneNumber: candidate.phoneNumber || candidate.phone_number || '',
    phoneVerificationCode:
      typeof phoneFallbackCode === 'number' || typeof phoneFallbackCode === 'string'
        ? String(phoneFallbackCode)
        : undefined,
    emailVerificationCode:
      typeof emailFallbackCode === 'number' || typeof emailFallbackCode === 'string'
        ? String(emailFallbackCode)
        : undefined,
  };
}

export const PhoneVerificationPage: FC = () => {
  const { t } = useTranslation('common');
  const VERIFY_MISSING_DATA_KEY = 'verify.missingData';
  const VERIFY_INVALID_CODE_KEY = 'verify.invalidCode';
  const VERIFY_GENERIC_ERROR_KEY = 'verify.genericError';
  const location = useLocation();
  const navigate = useNavigate();

  // Safely extract registrationData from location.state without using `any`.
  const rawState: unknown = location.state;
  let registrationData: RegistrationData | null = null;
  if (rawState && typeof rawState === 'object' && 'registrationData' in rawState) {
    const maybe = (rawState as { registrationData: unknown }).registrationData;
    if (isRegistrationData(maybe)) {
      registrationData = normalizeRegistrationData(maybe);
    }
  }
  const [verificationCode, setVerificationCode] = useState<string>(
    registrationData?.phoneVerificationCode ?? '',
  );
  const [error, setError] = useState<string>('');
  const [isResending, setIsResending] = useState<boolean>(false);

  const handleVerify = async (event_: FormEvent<HTMLFormElement>): Promise<void> => {
    event_.preventDefault();

    if (!registrationData) {
      setError(t(VERIFY_MISSING_DATA_KEY));
      return;
    }

    try {
      const response = await axios.post<ApiResponse>(`${API_BASE_URL}/api/auth/verify-phone`, {
        email: registrationData.email,
        phone_number: registrationData.phoneNumber,
        verificationCode,
      });

      if (response.data.success) {
        navigate('/email-verification', { state: { registrationData } });
      } else {
        setError(response.data.message ?? t(VERIFY_INVALID_CODE_KEY));
      }
    } catch {
      setError(t(VERIFY_GENERIC_ERROR_KEY));
    }
  };

  const handleResendCode = async (): Promise<void> => {
    if (!registrationData) {
      setError(t(VERIFY_MISSING_DATA_KEY));
      return;
    }

    setIsResending(true);
    try {
      const response = await axios.post<ApiResponse>(
        `${API_BASE_URL}/api/auth/resend-verification-code`,
        { email: registrationData.email, phone_number: registrationData.phoneNumber },
      );

      if (!response.data.success) {
        setError(response.data.message ?? t(VERIFY_GENERIC_ERROR_KEY));
      } else if (response.data.phoneVerificationCode) {
        setVerificationCode(String(response.data.phoneVerificationCode));
      }
    } catch {
      setError(t(VERIFY_GENERIC_ERROR_KEY));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="phone-verification-container">
      <h2>{t('verify.phoneTitle')}</h2>
      <p>
        {t('verify.enterCodeTo')} {registrationData?.phoneNumber ?? ''}
      </p>
      <form onSubmit={handleVerify}>
        <input
          type="text"
          placeholder={t('verify.codePlaceholder')}
          value={verificationCode}
          onChange={(event_: ChangeEvent<HTMLInputElement>) =>
            setVerificationCode(event_.target.value)
          }
        />
        <button type="submit">{t('verify.verify')}</button>
      </form>
      <p>
        {t('verify.didntReceive', "Didn’t receive an SMS?")}{' '}
        <button onClick={handleResendCode} disabled={isResending}>
          {isResending ? t('verify.sending') : t('verify.resend')}
        </button>
      </p>
      {error && <p className="error">{error}</p>}
    </div>
  );
};
